class ValidationSchema {
  constructor() {
    this.rules = {};
    this.errors = {};
  }

  field(fieldName, validators) {
    this.rules[fieldName] = validators;
    return this;
  }

  validateField(fieldName, value, data = {}) {
    const validators = this.rules[fieldName];
    if (!validators) return { valid: true, value: value };

    for (const validator of validators) {
      const result = validator(value, data);
      if (!result.valid) {
        return { valid: false, errors: [result.message] };
      }
    }

    return { valid: true, value: value };
  }

  validateForm(data) {
    this.errors = {};
    const validatedData = {};

    Object.keys(data).forEach(fieldName => {
      const result = this.validateField(fieldName, data[fieldName], data);
      
      if (result.valid) {
        validatedData[fieldName] = result.value;
      } else {
        this.errors[fieldName] = result.errors;
      }
    });

    return {
      valid: Object.keys(this.errors).length === 0,
      data: validatedData,
      errors: this.errors
    };
  }

  static required(message = "This field is required") {
    return (value) => {
      if (value === null || value === undefined || value.toString().trim() === '') {
        return { valid: false, message: message };
      }
      return { valid: true, value: value };
    };
  }

  static email(message = "Invalid email format") {
    return (value) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { valid: true, value: value };
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.toString())) {
        return { valid: false, message: message };
      }
      return { valid: true, value: value };
    };
  }

  static minLength(min, message) {
    return (value) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { valid: true, value: value };
      }
      
      if (value.toString().length < min) {
        return { valid: false, message: message || `Must be at least ${min} characters` };
      }
      return { valid: true, value: value };
    };
  }

  static maxLength(max, message) {
    return (value) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { valid: true, value: value };
      }
      
      if (value.toString().length > max) {
        return { valid: false, message: message || `Must be no more than ${max} characters` };
      }
      return { valid: true, value: value };
    };
  }

  static phone(message = "Invalid phone number") {
    return (value) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { valid: true, value: value };
      }
      
      const phoneRegex = /^[\d\-\+\(\)\s]+$/;
      if (!phoneRegex.test(value.toString())) {
        return { valid: false, message: message };
      }
      return { valid: true, value: value };
    };
  }

  static url(message = "Invalid URL format") {
    return (value) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { valid: true, value: value };
      }
      
      try {
        new URL(value.toString());
        return { valid: true, value: value };
      } catch {
        return { valid: false, message: message };
      }
    };
  }

  static oneOf(options, message = "Invalid selection") {
    return (value) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { valid: true, value: value };
      }
      
      if (!options.includes(value)) {
        return { valid: false, message: message };
      }
      return { valid: true, value: value };
    };
  }

  static custom(validator, message) {
    return (value, data = {}) => {
      try {
        const result = validator(value, data);
        return { valid: result, value: value, message: message };
      } catch (error) {
        return { valid: false, message: message || error.message };
      }
    };
  }
}

// Advertisement Campaign Validation Schema
class AdvertisementValidationSchema extends ValidationSchema {
  constructor() {
    super();
    this.setupRules();
  }

  setupRules() {
    this
      .field('company_name', [
        ValidationSchema.required("会社名をご入力ください。"),
      ])
      .field('individual_name', [
        ValidationSchema.required("個人名をご入力ください。"),
      ])
      .field('prefecture', [
        ValidationSchema.required("都道府県を選択してください。")
      ])
      .field('city', [
        ValidationSchema.required("市町村を選択してください。")
      ])
      .field('address', [
        ValidationSchema.required("住所を入力してください。"),
      ])
      .field('contact_person_name', [
        ValidationSchema.required("担当者名を入力してください。"),
      ])
      .field('contact_email', [
        ValidationSchema.required("メールアドレスを入力してください。"),
        ValidationSchema.email("メールアドレスを正しく入力してください。")
      ])
      .field('contact_phone', [
        ValidationSchema.required("電話番号を入力してください。"),
        ValidationSchema.phone("電話番号を正しく入力してください。"),
        ValidationSchema.minLength(10, "電話番号を正しく入力してください。"),
        ValidationSchema.maxLength(11, "電話番号を正しく入力してください。")
      ])
      .field('link', [
        ValidationSchema.required("URLを正しく入力してください。"),
        ValidationSchema.url("URLを正しく入力してください。")
      ])
      .field('content', [
        ValidationSchema.required("広告内容を入力してください。"),
      ])
      .field('banner_pc_image', [
        ValidationSchema.custom(
          (value, formData) => {
            if (formData && formData.existing_pc_image === 'true') {
              return true;
            }
            if (!value || value.length === 0) {
              return false;
            }
            return true;
          },
          "PC用バナー画像をアップロードしてください。"
        )
      ])
      .field('banner_mobile_image', [
        ValidationSchema.custom(
          (value, formData) => {
            if (formData && formData.existing_mobile_image === 'true') {
              return true;
            }
            if (!value || value.length === 0) {
              return false;
            }
            return true;
          },
          "モバイル用バナー画像をアップロードしてください。"
        )
      ])
      .field('placement_position', [
        ValidationSchema.required("掲載位置をご選択ください。"),
        ValidationSchema.custom(
          (value) => value && value.length > 0,
          "掲載位置をご選択ください。"
        )
      ])
  }

  validateFormWithConditions(data) {
    const validationData = { ...data };
    
    if (!validationData.owner_type) {
      return this.validateForm(validationData);
    }
    
    if (validationData.owner_type === 'company') {
      delete validationData.individual_name;
    } else if (validationData.owner_type === 'individual') {
      delete validationData.company_name;
    }
    
    return this.validateForm(validationData);
  }
}

window.ValidationSchema = ValidationSchema;
window.AdvertisementValidationSchema = AdvertisementValidationSchema;